import { useEffect, useRef, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue';

/** Server search debounce — one request per typing pause, not per keystroke. */
export const SEARCH_DEBOUNCE_MS = 300;

export interface RosterSearch {
  /** Raw input value — bind the search box to this for instant typing feedback. */
  query: string;
  setQuery: (q: string) => void;
  /** Any non-whitespace search text is present. */
  isSearching: boolean;
  /**
   * True when the active bucket is already fully loaded on the page
   * (total <= pageSize), so search is a client-side filter — no server round-trip,
   * instant AND complete.
   */
  clientMode: boolean;
  /**
   * Term to send to the server as `?search=`. Debounced, and `undefined` in
   * client mode or when empty — so the page query key stays STABLE while typing
   * a small bucket (no refetch), and fires at most once per pause on a large one.
   */
  serverSearch: string | undefined;
  /**
   * Lowercased term to filter loaded rows against in client mode (instant, no
   * debounce). Empty string when not client-filtering (server already filtered,
   * or not searching) — a primitive so callers can list it directly in useMemo
   * deps. Use as: `clientQuery === '' ? rows : rows.filter(r => getText(r).toLowerCase().includes(clientQuery))`.
   */
  clientQuery: string;
}

/**
 * Hybrid roster search: instant client-side filtering when the active pill's
 * whole set is already on the page (`total <= pageSize`), debounced server-side
 * `?search=` when it spans multiple pages. This is the industry-standard shape
 * for medium datasets — it kills the per-keystroke request storm (and the
 * skeleton flash it caused) while keeping search complete across large buckets.
 *
 * `activeTotal` is the active pill's count from `/roster/counts`. When it's still
 * loading (`undefined`) we fall back to server search so a match is never missed.
 */
export function useRosterSearch(
  activeTotal: number | undefined,
  pageSize: number,
  options?: {
    /** Called whenever the (trimmed) query CHANGES — pass `() => setPage(1)` so a
     *  new search always lands on page 1. Without it, searching while on page ≥2
     *  slices the (much smaller) result set at the old offset and shows an empty
     *  page. Also fires on clearing the search (back to page 1 — predictable). */
    onQueryChange?: () => void;
    /** Seeds the box, e.g. from a URL-owned search term. */
    initialQuery?: string;
  },
): RosterSearch {
  const [query, setQuery] = useState(options?.initialQuery ?? '');
  const trimmed = query.trim();
  const isSearching = trimmed !== '';
  const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);

  // Reset pagination when the search term changes (not on mount — the ref is
  // seeded with the first render's value, so a deep link like ?page=3 holds).
  const onQueryChange = options?.onQueryChange;
  const prevTrimmedRef = useRef(trimmed);
  useEffect(() => {
    if (prevTrimmedRef.current === trimmed) return;
    prevTrimmedRef.current = trimmed;
    onQueryChange?.();
  }, [trimmed, onQueryChange]);

  const clientMode =
    isSearching && activeTotal !== undefined && activeTotal <= pageSize;

  return {
    query,
    setQuery,
    isSearching,
    clientMode,
    serverSearch: clientMode || debounced === '' ? undefined : debounced,
    clientQuery: clientMode ? trimmed.toLowerCase() : '',
  };
}
